import {iframeToSdkChannel} from "../messaging/IframeToSdkChannel";
import {IFrameMessages} from "../messaging/IFrameMessages";
import {isDeepEqual} from "../../blinkpay/UtilsLib";
import {Dispatcher} from "../../stem-core/base/Dispatcher";
import {merchantLocalStorage, merchantSessionStorage} from "./merchant-state/MerchantStorage";
import {DONATION_COOLDOWN_PERIOD, EMPTY_USER_DATA, IFRAME_APP_TYPE, MERCHANT_STORAGE_KEYS, type SdkUserData} from "../Constants";
import {allIFrameElements} from "../ui/elements/IFrameElement";

interface RecurringPayment {
    activeUntil?: string;
}

interface MerchantToken {
    key: string;
    expiresAt?: string;
}

function isActiveRecurringPayment(recurringPayment: RecurringPayment | null, cooldownPeriod = 0): boolean {
    return recurringPayment?.activeUntil
        && Date.now() - (new Date(recurringPayment.activeUntil)).getTime() < cooldownPeriod;
}

function hasActiveDonation(userData: SdkUserData): boolean {
    // TODO @Mihai we should also see if there was a recurring donation withing the last DONATION_COOLDOWN_PERIOD as well
    return isActiveRecurringPayment(userData.activeRecurringDonation)
            || isActiveRecurringPayment(userData.latestOneTimeDonation, DONATION_COOLDOWN_PERIOD);
}

// Manages the user authentication info.
export const userDataModule = {
    userPreferencesChange: new Dispatcher(),
    authenticationChange: new Dispatcher(),
    subscriptionChange: new Dispatcher(),

    userData: EMPTY_USER_DATA,

    init(): void {
        iframeToSdkChannel.addListener(IFrameMessages.UPDATE_SDK_USER_DATA, ({userData}: {userData: SdkUserData}) => {
            const oldUserData = {...this.userData};

            Object.assign(this.userData, userData);

            if (oldUserData.userId !== userData.userId) {
                this.authenticationChange.dispatch();
            }

            if (!isDeepEqual(oldUserData.subscription, userData.subscription)) {
                this.subscriptionChange.dispatch(userData.subscription);
            }

            // Since most journey conditions listen for this, add as last
            if (!isDeepEqual(oldUserData, userData)) {
                this.userPreferencesChange.dispatch();
            }
        });

        iframeToSdkChannel.addListener(IFrameMessages.SET_MERCHANT_TOKEN, ({token}: {token: MerchantToken | null}) => this.setMerchantToken(token));
    },

    setMerchantToken(token: MerchantToken | null): void {
        if (token) {
            merchantLocalStorage.setItem(MERCHANT_STORAGE_KEYS.authToken, token);
            merchantSessionStorage.setItem(MERCHANT_STORAGE_KEYS.authToken, token);
        } else {
            merchantLocalStorage.removeItem(MERCHANT_STORAGE_KEYS.authToken);
            merchantSessionStorage.removeItem(MERCHANT_STORAGE_KEYS.authToken);
        }
        // Every iframe app runs its own UserService, so left alone they'd each fetch the user data for
        // the new token. Only one is asked to, and it shares the response with the others. The widget
        // is the natural pick: it's mounted first and always initialized by the time a token changes.
        const iFrameElements = [...allIFrameElements];
        const fetcher = iFrameElements.find(el => el.appType === IFRAME_APP_TYPE.widget) || iFrameElements[0];
        for (const iFrameElement of iFrameElements) {
            iFrameElement.sendMessage(IFrameMessages.UPDATE_MERCHANT_TOKEN, {
                token,
                fetchUserData: iFrameElement === fetcher,
            });
        }
    },

    isAuthenticated(): boolean {
        return this.userData.userId != null;
    },

    isRecurringDonor(): boolean {
        return isActiveRecurringPayment(this.userData.activeRecurringDonation);
    },

    isDonor(): boolean {
        return hasActiveDonation(this.userData);
    },

    isSubscriber(): boolean {
        return isActiveRecurringPayment(this.userData.subscription);
    },

    // DO NOT DELETE, this gets exposed below
    // TODO @cleanup mark with decorator
    isNewsletterSubscriber(newsletterId: string | { newsletterId: string }): boolean {
        newsletterId = (typeof newsletterId === "object" ? newsletterId.newsletterId : null) || newsletterId as string; // TODO @mustfix temp hack to handle having this from options in journeys
        return this.userData.newsletterIds.includes(newsletterId);
    },

    // Marks all methods that should be exposed in journey/merchant function
    exposeMerchantFunctions() {
        // TODO @cleanup should use a decorator explicitly on these methods
        const methods: Record<string, Function> = {};
        for (const key in this) {
            if (key.startsWith("is")) {
                methods[key] = (...args: any[]) => !!this[key](...args); // Ensure a bool is returned
            }
        }

        return methods;
    },
}
